/**
 * Multi-currency pricing configuration
 * Supports EUR, USD, GBP with automatic locale detection
 */

export type Currency = 'EUR' | 'USD' | 'GBP';
export type BillingPeriod = 'monthly' | 'yearly';

export interface CurrencyConfig {
  monthly: number;
  yearly: number;
  symbol: string;
  position: 'before' | 'after'; // Symbol position relative to amount
  locale: string; // For number formatting
}

/**
 * Pricing table - all currencies use the same numeric values
 * 1.20/month, 12.00/year (2 months free on yearly)
 */
export const PRICING: Record<Currency, CurrencyConfig> = {
  EUR: { 
    monthly: 1.20, 
    yearly: 12.00, 
    symbol: '€', 
    position: 'after',
    locale: 'fr-FR'
  },
  USD: { 
    monthly: 1.20, 
    yearly: 12.00, 
    symbol: '$', 
    position: 'before',
    locale: 'en-US'
  },
  GBP: { 
    monthly: 1.20, 
    yearly: 12.00, 
    symbol: '£', 
    position: 'before',
    locale: 'en-GB'
  },
};

/**
 * Detect currency based on browser locale
 * Falls back to EUR if locale is unknown
 */
export const getCurrencyFromLocale = (): Currency => {
  if (typeof navigator === 'undefined') return 'EUR';
  
  const language = navigator.language || 'en';
  
  // UK English → GBP
  if (language === 'en-GB') return 'GBP';
  
  // US/other English → USD
  if (language.startsWith('en-US') || language === 'en') return 'USD';
  
  // Eurozone languages → EUR
  const euroLocales = ['fr', 'de', 'es', 'it', 'pt', 'nl', 'be', 'at', 'ie', 'fi', 'el', 'sk', 'si', 'ee', 'lv', 'lt', 'mt', 'cy', 'lu'];
  const langCode = language.split('-')[0].toLowerCase();
  if (euroLocales.includes(langCode)) return 'EUR';
  
  // Default fallback → EUR
  return 'EUR';
};

/**
 * Format price with correct symbol position
 * EUR: "1.20€" | USD: "$1.20" | GBP: "£1.20"
 */
export const formatPrice = (amount: number, currency: Currency): string => {
  const config = PRICING[currency];
  const formattedAmount = amount.toFixed(2);
  
  if (config.position === 'before') {
    return `${config.symbol}${formattedAmount}`;
  } else {
    return `${formattedAmount}${config.symbol}`;
  }
};

/**
 * Get formatted prices for display
 */
export const getFormattedPrices = (currency: Currency) => {
  const config = PRICING[currency];
  const yearlyFull = config.monthly * 12; // What it would cost monthly
  
  return {
    monthly: formatPrice(config.monthly, currency),
    yearly: formatPrice(config.yearly, currency),
    yearlyStrikethrough: formatPrice(yearlyFull, currency),
    symbol: config.symbol,
    currency,
  };
};

/**
 * Get price for subscription button text
 */
export const getSubscribeButtonText = (period: BillingPeriod, currency: Currency): string => {
  const config = PRICING[currency];
  const amount = period === 'monthly' ? config.monthly : config.yearly;
  const suffix = period === 'monthly' ? '/month' : '/year';
  return `${formatPrice(amount, currency)}${suffix}`;
};
